<script setup lang="ts">
import { DronecastCaster } from '@/sdk'
import { kurentoUtils } from '@/utils/kurentoUtils'
import { useDronecastStore } from '@/stores/dronecastStore'
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const store = useDronecastStore()
const websocketUrl = computed(() => store.websocketUrl)

const gcs = route.query.gcs as string || ''
const drone = route.query.drone as string || ''
const camera = route.query.camera as string || ''
const hasParams = !!(gcs && drone && camera)

const caster = ref<InstanceType<typeof DronecastCaster> | null>(null)
const castingState = ref<'idle' | 'casting' | 'error' | 'retrying'>('idle')
const retryCount = ref(0)
const maxRetry = 10

function startCasting() {
  castingState.value = 'casting'
  caster.value?.cast({ gcs, drone, camera })
}

onMounted(() => {
  if (hasParams) {
    setTimeout(() => startCasting(), 300)
  }
})
</script>

<template>
  <div class="service-fullscreen service-cast-layout">
    <div v-if="!hasParams" class="service-error">
      <p>URL 파라미터가 누락되었습니다.</p>
      <p class="hint">?gcs=...&amp;drone=...&amp;camera=... 형식으로 접근하세요.</p>
    </div>

    <template v-else>
      <div class="cast-status">
        <span class="dot" :class="castingState"></span>
        <span v-if="castingState === 'casting'">Casting 중</span>
        <span v-else-if="castingState === 'retrying'">재연결 중 ({{ retryCount }}/{{ maxRetry }})</span>
        <span v-else-if="castingState === 'error'">오류</span>
        <span v-else>대기</span>
        <span class="cast-info">{{ gcs }} / {{ drone }} / {{ camera }}</span>
      </div>

      <div class="cast-preview">
        <DronecastCaster
          ref="caster"
          :castroomId="{ gcs, drone, camera }"
          :wsUrl="websocketUrl"
          :kurentoUtils="kurentoUtils"
          source="webcam"
          :showPreview="true"
          mediaServerType="headless"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.service-fullscreen {
  width: 100vw;
  height: 100vh;
  background: #111;
  color: #fff;
}
.service-cast-layout {
  display: flex;
  flex-direction: column;
}
.cast-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 14px;
}
.cast-info {
  margin-left: auto;
  font-size: 12px;
  color: #888;
}
.cast-preview {
  flex: 1;
  overflow: hidden;
}
.service-error {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #aaa;
  font-size: 14px;
}
.hint { font-size: 12px; color: #666; margin-top: 8px; }
</style>
